import { useEffect, useMemo, useRef, useState } from "react";
import Modal from "../../UI/Modal";
import { Button } from "../../UI/Button";
import { companyKpiUseCase } from "../../../../di/container";
import {
  KPI_TYPE_OPTIONS,
  allowedIntervals,
  presetRange,
  graphSeriesLabel,
  isManualMetric,
  metricOption,
  type ComputeResponse,
  type CompanyKpiMachine,
  type GoalDirection,
  type KpiChartType,
  type KpiInterval,
  type KpiMachineScope,
  type KpiPeriodPreset,
  type MesDashboardCard,
  type SaveCardRequest,
} from "../../../../domain/types/company/company-kpi";
import { ComputeChart } from "./ComputeChart";

interface Props {
  initial: MesDashboardCard | null;
  machines: CompanyKpiMachine[];
  /** 이미 등록된 지표(다른 카드들) — 새 카드 선택지에서 제외(지표당 1카드). */
  usedMetricTypes?: string[];
  onClose: () => void;
  onSaved: () => void;
}

// 전체 단위 노출 — 실제 선택 가능 여부는 조회 기간(allowedIntervals)으로 disable.
const INTERVALS: KpiInterval[] = ["MINUTE", "HOUR", "DAY", "WEEK", "MONTH"];
const INTERVAL_LABEL: Record<KpiInterval, string> = { MINUTE: "분", HOUR: "시간", DAY: "일", WEEK: "주", MONTH: "월" };
const PRESETS: KpiPeriodPreset[] = ["1D", "7D", "30D", "3M", "6M", "12M"];
const PRESET_LABEL: Record<KpiPeriodPreset, string> = {
  "1D": "오늘",
  "7D": "7일",
  "30D": "30일",
  "3M": "3개월",
  "6M": "6개월",
  "12M": "12개월",
  CUSTOM: "기간입력",
};

const SELECT_CLS =
  "border border-[#e0e0e0] rounded-[8px] px-2 py-1.5 text-[13px] focus:border-[#4285F4] focus:outline-none";
const NUM_CLS =
  "border border-[#e0e0e0] rounded-[8px] px-2 py-1.5 text-[13px] w-full focus:border-[#4285F4] focus:outline-none";

const numToStr = (n: number | null | undefined) => (n == null ? "" : String(n));
const strToNum = (s: string): number | null => {
  const t = s.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

export const CardEditorModal = ({
  initial,
  machines,
  usedMetricTypes,
  onClose,
  onSaved,
}: Props) => {
  const m0 = initial?.metrics[0];
  // 이미 등록된 지표 제외(수정 중인 카드 자신의 지표는 유지).
  const used = new Set((usedMetricTypes ?? []).filter((t) => t !== m0?.metricType));
  const availableOptions = KPI_TYPE_OPTIONS.filter((o) => !used.has(o.value));
  const [metricType, setMetricType] = useState<string>(
    m0?.metricType ?? availableOptions[0]?.value ?? KPI_TYPE_OPTIONS[0].value,
  );
  const opt = metricOption(metricType);
  const manual = isManualMetric(metricType);
  const unit = opt?.unit ?? "";

  // 카드 제목(옵션). 비우면 지표 이름으로 폴백 표시.
  const [title, setTitle] = useState<string>(initial?.title ?? "");

  const [direction, setDirection] = useState<GoalDirection>(
    (m0?.goalDirection as GoalDirection) ?? (opt?.direction as GoalDirection) ?? "INCREASE",
  );
  const [baseValue, setBaseValue] = useState(numToStr(m0?.baseValue));
  const [goalValue, setGoalValue] = useState(numToStr(m0?.goalValue));
  const [manualValue, setManualValue] = useState(numToStr(m0?.manualValue));
  // 벤치마크(현재값·목표값) 사용 여부. 끄면 입력 접고 달성률 없이 그래프/현재값만.
  const [useBenchmark, setUseBenchmark] = useState<boolean>(
    initial ? m0?.baseValue != null && m0?.goalValue != null : true,
  );

  // 계산형 전용
  const [machineScope, setMachineScope] = useState<KpiMachineScope>(
    (m0?.machineScope as KpiMachineScope) ?? "ALL",
  );
  const [machineIds, setMachineIds] = useState<number[]>(m0?.machineIds ?? []);
  const [interval, setInterval] = useState<KpiInterval>(
    (initial?.interval as KpiInterval) ?? "DAY",
  );
  const initPreset = (initial?.periodPreset as KpiPeriodPreset) ?? "30D";
  const initRange = presetRange(initPreset);
  const [periodPreset, setPeriodPreset] = useState<KpiPeriodPreset>(initPreset);
  // 날짜창 상시 노출 — 저장된 날짜 우선, 없으면 프리셋 범위로 채움.
  const [customStart, setCustomStart] = useState(initial?.customStart ?? initRange?.start ?? "");
  const [customEnd, setCustomEnd] = useState(initial?.customEnd ?? initRange?.end ?? "");

  // 프리셋 클릭 → 하이라이트 + 날짜창 자동 채움. 날짜 직접 수정 → CUSTOM 전환(하이라이트 해제).
  const selectPreset = (p: KpiPeriodPreset) => {
    setPeriodPreset(p);
    const r = presetRange(p);
    if (r) {
      setCustomStart(r.start);
      setCustomEnd(r.end);
    }
  };
  const editStart = (v: string) => {
    setCustomStart(v);
    setPeriodPreset("CUSTOM");
  };
  const editEnd = (v: string) => {
    setCustomEnd(v);
    setPeriodPreset("CUSTOM");
  };
  const [chartType, setChartType] = useState<KpiChartType>(
    (initial?.chartType as KpiChartType) ?? "BAR",
  );

  // 조회 기간 → 허용 단위. 무효 단위는 select 에서 disable, 현재 단위가 무효가 되면 자동 스냅(일 우선, 없으면 가장 굵은 단위).
  const allowed = allowedIntervals(periodPreset, customStart, customEnd);
  useEffect(() => {
    if (!allowed.includes(interval)) {
      setInterval(allowed.includes("DAY") ? "DAY" : allowed[allowed.length - 1]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodPreset, customStart, customEnd]);

  const [preview, setPreview] = useState<ComputeResponse | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // 미리보기도 카드와 동일: AVG 지표 + 자료 없음 + 목표값 → 목표값 평탄선(SUM은 0 그대로).
  const previewResponse = useMemo<ComputeResponse | null>(() => {
    if (!preview) return null;
    const aggregate = opt?.aggregate ?? "AVG";
    const goalNum = useBenchmark ? strToNum(goalValue) : null;
    const hasReal = preview.series.some((s) => s.points.length > 0);
    if (aggregate === "AVG" && !hasReal && goalNum != null && preview.series.length) {
      return {
        ...preview,
        series: preview.series.map((s) => ({
          ...s,
          points: preview.buckets.map((b) => ({ bucketStart: b, value: goalNum })),
        })),
      };
    }
    return preview;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, metricType, useBenchmark, goalValue]);

  // 지표 변경 시 목표 방향을 해당 지표 기본값으로 동기화
  const onMetricChange = (next: string) => {
    setMetricType(next);
    const o = metricOption(next);
    if (o) setDirection(o.direction as GoalDirection);
  };

  const previewReady =
    !manual &&
    !(periodPreset === "CUSTOM" && (!customStart || !customEnd)) &&
    !(machineScope === "SPECIFIC" && machineIds.length === 0);

  // 라이브 프리뷰 (계산형, 디바운스)
  const machineIdsKey = JSON.stringify(machineIds);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!previewReady) {
      setPreview(null);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        setPreviewError(null);
        setPreview(
          await companyKpiUseCase.compute({
            interval,
            periodPreset,
            customStart: periodPreset === "CUSTOM" ? customStart : undefined,
            customEnd: periodPreset === "CUSTOM" ? customEnd : undefined,
            metrics: [
              {
                label: graphSeriesLabel(metricType, opt?.label),
                sbizType: metricType,
                machineScope,
                machineIds: machineScope === "SPECIFIC" ? machineIds : undefined,
              },
            ],
          }),
        );
      } catch (e) {
        setPreviewError(e instanceof Error ? e.message : "미리보기 실패");
        setPreview(null);
      }
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    metricType,
    machineScope,
    machineIdsKey,
    interval,
    periodPreset,
    customStart,
    customEnd,
    previewReady,
  ]);

  const toggleMachine = (id: number) =>
    setMachineIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const handleSave = async () => {
    if (!manual && machineScope === "SPECIFIC" && machineIds.length === 0) {
      setSaveError("장비를 1개 이상 선택하세요.");
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const body: SaveCardRequest = {
        // 비우면 null → 표시 시 지표 이름으로 폴백.
        title: title.trim() || null,
        interval: manual ? "DAY" : interval,
        periodPreset: manual ? "30D" : periodPreset,
        customStart: !manual && periodPreset === "CUSTOM" ? customStart : undefined,
        customEnd: !manual && periodPreset === "CUSTOM" ? customEnd : undefined,
        chartType: manual ? "BAR" : chartType,
        metrics: [
          {
            metricType,
            machineScope: manual ? "ALL" : machineScope,
            machineIds: !manual && machineScope === "SPECIFIC" ? machineIds : undefined,
            baseValue: useBenchmark ? strToNum(baseValue) : null,
            goalValue: useBenchmark ? strToNum(goalValue) : null,
            goalDirection: useBenchmark ? direction : null,
            manualValue: manual ? strToNum(manualValue) : undefined,
          },
        ],
      };
      if (initial) await companyKpiUseCase.updateCard(initial.id, body);
      else await companyKpiUseCase.createCard(body);
      onSaved();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "저장 실패");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal setIsModalVisible={() => onClose()} title={initial ? "KPI 수정" : "KPI 추가"}>
      <div className="w-[640px] max-h-[80vh] overflow-y-auto flex flex-col gap-4 text-[#222]">
        {/* 카드 제목 (옵션 — 비우면 지표 이름으로 표시) */}
        <div className="flex flex-col gap-1">
          <label className="text-[12px] text-[#555] font-medium">카드 제목 (선택)</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={`비우면 지표 이름 사용 (${opt?.label ?? metricType})`}
            maxLength={200}
            className={SELECT_CLS}
          />
        </div>

        {/* 지표 */}
        <div className="flex flex-col gap-1">
          <label className="text-[12px] text-[#555] font-medium">지표</label>
          <select
            value={metricType}
            onChange={(e) => onMetricChange(e.target.value)}
            className={SELECT_CLS}
          >
            {availableOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
                {o.computable ? "" : " (수동)"}
              </option>
            ))}
          </select>
        </div>

        {/* 벤치마크 사용 토글 */}
        <label className="flex items-center gap-2 text-[13px] text-[#555] cursor-pointer select-none">
          <input
            type="checkbox"
            checked={useBenchmark}
            onChange={(e) => setUseBenchmark(e.target.checked)}
          />
          현재값·목표값(벤치마크) 사용 — 달성률 표시
        </label>

        {/* 벤치마크: 현재값/목표값/방향 (사용 시에만) */}
        {useBenchmark && (
          <div className="grid grid-cols-3 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-[12px] text-[#555] font-medium">현재값(도입 전){unit && ` (${unit})`}</label>
              <input value={baseValue} onChange={(e) => setBaseValue(e.target.value)} className={NUM_CLS} inputMode="decimal" />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[12px] text-[#555] font-medium">목표값{unit && ` (${unit})`}</label>
              <input value={goalValue} onChange={(e) => setGoalValue(e.target.value)} className={NUM_CLS} inputMode="decimal" />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[12px] text-[#555] font-medium">목표 방향</label>
              <select
                value={direction}
                onChange={(e) => setDirection(e.target.value as GoalDirection)}
                className={SELECT_CLS}
              >
                <option value="INCREASE">증가(클수록 좋음)</option>
                <option value="DECREASE">감소(작을수록 좋음)</option>
              </select>
            </div>
          </div>
        )}

        {manual ? (
          <div className="flex flex-col gap-1">
            <label className="text-[12px] text-[#555] font-medium">
              실측값{unit && ` (${unit})`} — 수동입력 지표
            </label>
            <input
              value={manualValue}
              onChange={(e) => setManualValue(e.target.value)}
              className={NUM_CLS}
              inputMode="decimal"
              placeholder="현재 측정값을 직접 입력"
            />
            <p className="text-[11.5px] text-[#888]">
              이 지표는 자동 계산(그래프) 대상이 아니라 회사가 실측값을 직접 입력합니다.
            </p>
          </div>
        ) : (
          <>
            {/* 장비 범위 */}
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-[12px] text-[#555] font-medium">장비</label>
              <select
                value={machineScope}
                onChange={(e) => setMachineScope(e.target.value as KpiMachineScope)}
                className={SELECT_CLS}
              >
                <option value="ALL">전체 장비</option>
                <option value="SPECIFIC">장비 선택</option>
              </select>
              {machineScope === "SPECIFIC" && (
                <div className="flex flex-wrap gap-2">
                  {machines.length === 0 && (
                    <span className="text-[12px] text-[#888]">등록된 장비 없음</span>
                  )}
                  {machines.map((mc) => (
                    <label key={mc.id} className="flex items-center gap-1 text-[12px] text-[#555]">
                      <input
                        type="checkbox"
                        checked={machineIds.includes(mc.id)}
                        onChange={() => toggleMachine(mc.id)}
                      />
                      {mc.name}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* 기간 칩 + 주기/차트 */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="inline-flex items-center rounded-[8px] border border-[#e0e0e0] overflow-hidden">
                {PRESETS.map((p) => (
                  <button
                    key={p}
                    onClick={() => selectPreset(p)}
                    className={`px-3 py-[7px] text-[12.5px] border-r border-[#e0e0e0] last:border-r-0 transition-colors ${
                      periodPreset === p
                        ? "bg-[#4285F4] text-white"
                        : "bg-white text-[#555] hover:bg-[#F5F5F5]"
                    }`}
                  >
                    {PRESET_LABEL[p]}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <select value={interval} onChange={(e) => setInterval(e.target.value as KpiInterval)} className={SELECT_CLS}>
                  {INTERVALS.map((iv) => (
                    <option key={iv} value={iv} disabled={!allowed.includes(iv)}>
                      {INTERVAL_LABEL[iv]}
                      {!allowed.includes(iv) ? " (기간 제한)" : ""}
                    </option>
                  ))}
                </select>
                <select value={chartType} onChange={(e) => setChartType(e.target.value as KpiChartType)} className={SELECT_CLS}>
                  <option value="LINE">선</option>
                  <option value="BAR">막대</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input type="date" value={customStart} onChange={(e) => editStart(e.target.value)} className={SELECT_CLS} />
              <span className="text-[#888]">~</span>
              <input type="date" value={customEnd} onChange={(e) => editEnd(e.target.value)} className={SELECT_CLS} />
            </div>

            {/* 미리보기 */}
            <div className="border border-[#eee] rounded-[8px] p-2">
              {previewError ? (
                <div className="flex items-center justify-center h-[200px] text-[13px] text-[#F24A4A]">{previewError}</div>
              ) : preview ? (
                <ComputeChart response={previewResponse ?? preview} chartType={chartType} height={200} />
              ) : (
                <div className="flex items-center justify-center h-[200px] text-[13px] text-[#888]">
                  {previewReady ? "계산 중…" : "기간/장비를 설정하면 미리보기가 표시됩니다"}
                </div>
              )}
            </div>
          </>
        )}

        {saveError && <div className="text-[13px] text-[#F24A4A]">{saveError}</div>}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={onClose}>
            취소
          </Button>
          <Button variant="primary" onClick={handleSave} loading={saving}>
            저장
          </Button>
        </div>
      </div>
    </Modal>
  );
};
